import React, { useEffect } from 'react';

import Animated, {
  FadeIn,
  LayoutAnimationConfig,
  SlideInLeft,
  SlideInRight,
} from 'react-native-reanimated';

import { IPicker, IPickerAspect } from '@rbi-ctg/menu';
import {
  maybePlayAudioOnce,
  useMaybePlayAudioOnceOnMount,
} from 'components/app-takeover/play-audio-once';
import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import { FullWidthAutoHeightImage } from 'components/picture';
import { MenuObjectTypes } from 'enums/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep, UserSelectionModifierSlotMap } from 'state/product-wizard-v2/types';
import { primitive, spacing } from 'styles/constants';
import { pickerAspectOptHasResolution } from 'utils/menu';

import { ProductCustomization } from '../product-customization';

import { FADE_IN_ANIMATION_DURATION, SLIDE_IN_ANIMATION_DURATION } from './constants';
import { CloseConfirmationDialog } from './dialogs/close-confirmation-dialog';
import { ProductDetailHome } from './product-home-flow';
import { DEFAULT_SLOT_KEY } from './product-item-flow/constants';
import { ProductOrderReview } from './product-order-review';
import { ProductDetailFlowContent, productDetailFlowContainerStyles } from './styled';

export const ProductDetailFlow: React.FC = () => {
  const isMountedRef = React.useRef<boolean>(false);
  const { currentStep, slotKeyToCustomize } = useProductWizardFlow();
  const {
    menuObject,
    userSelections,
    isBackNavigation,
    setIsBackNavigation,
  } = useProductWizardContext();

  const {
    backgroundColor: takeoverBg,
    backgroundImage,
    audio,
    additionalAudioSteps,
  } = useAppTakeoverPdp(menuObject?._id);

  useMaybePlayAudioOnceOnMount(audio);

  useEffect(() => {
    maybePlayAudioOnce((additionalAudioSteps as any)?.[currentStep]);
  }, [additionalAudioSteps, currentStep]);

  const SLOT = menuObject._type === MenuObjectTypes.ITEM ? DEFAULT_SLOT_KEY : slotKeyToCustomize;

  const [modifierSelections, setModifierSelections] = React.useState<UserSelectionModifierSlotMap>(
    userSelections.modifiers[SLOT]
  );

  const [selectedPickerAspects, setSelectedPickerAspects] = React.useState(
    userSelections.pickerAspects || {}
  );

  const pickerData = menuObject as IPicker;

  const getPreviousPickerSelectionIdentifiers = React.useCallback(
    (startPoint: number, endPoint: number) =>
      pickerData.pickerAspects
        .slice(startPoint, endPoint)
        .map(aspect => selectedPickerAspects?.[aspect._id]),
    [pickerData.pickerAspects, selectedPickerAspects]
  );

  const filteredPickerAspects = React.useMemo<IPickerAspect[]>(
    () =>
      (pickerData.pickerAspects ?? []).map((currentAspect, index) => {
        const resolvedOpts = currentAspect.pickerAspectOptions.filter(option =>
          pickerAspectOptHasResolution(
            option.identifier,
            getPreviousPickerSelectionIdentifiers(0, index),
            pickerData.options
          )
        );
        return {
          ...currentAspect,
          pickerAspectOptions: resolvedOpts,
        };
      }),
    [getPreviousPickerSelectionIdentifiers, pickerData.options, pickerData.pickerAspects]
  );

  const renderContentByWizardStep = React.useCallback(() => {
    switch (currentStep) {
      case ProductWizardStep.ProductEditOrder:
      case ProductWizardStep.ProductCustomization:
        return (
          <ProductCustomization
            currentStep={currentStep}
            filteredPickerAspects={filteredPickerAspects}
            getPreviousPickerSelectionIdentifiers={getPreviousPickerSelectionIdentifiers}
            selectedPickerAspects={selectedPickerAspects}
            setSelectedPickerAspects={setSelectedPickerAspects}
          />
        );
      case ProductWizardStep.ProductReview:
        return <ProductOrderReview modifierSelections={modifierSelections} />;
      default:
        return (
          <ProductDetailHome
            modifierSelections={modifierSelections}
            handleChangeModifierSelections={setModifierSelections}
            filteredPickerAspects={filteredPickerAspects}
            getPreviousPickerSelectionIdentifiers={getPreviousPickerSelectionIdentifiers}
            selectedPickerAspects={selectedPickerAspects}
            setSelectedPickerAspects={setSelectedPickerAspects}
          />
        );
    }
  }, [
    currentStep,
    filteredPickerAspects,
    getPreviousPickerSelectionIdentifiers,
    modifierSelections,
    selectedPickerAspects,
  ]);

  const productDetailFlowKey = React.useMemo(() => `${currentStep}-${slotKeyToCustomize}`, [
    currentStep,
    slotKeyToCustomize,
  ]);

  // LayoutAnimationConfig skipEntering logic for web devices
  React.useLayoutEffect(() => {
    isMountedRef.current = true;
  });

  React.useEffect(() => {
    return () => {
      setIsBackNavigation(false);
    };
  }, [productDetailFlowKey, setIsBackNavigation]);

  const Animation = isBackNavigation ? SlideInLeft : SlideInRight;

  return (
    <>
      {/* This LayoutAnimationConfig will prevent the animation being triggered on mount. */}
      <LayoutAnimationConfig skipEntering>
        <Animated.View
          key={productDetailFlowKey}
          style={productDetailFlowContainerStyles}
          entering={FadeIn.duration(FADE_IN_ANIMATION_DURATION)}
        >
          <Animated.View
            entering={
              isMountedRef.current ? Animation.duration(SLIDE_IN_ANIMATION_DURATION) : undefined
            }
            style={{
              ...productDetailFlowContainerStyles,
              backgroundColor: takeoverBg || primitive.$white,
            }}
          >
            <ProductDetailFlowContent testID="product-detail-flow-content">
              {backgroundImage && currentStep !== ProductWizardStep.ProductHome && (
                <FullWidthAutoHeightImage
                  source={backgroundImage}
                  style={{ position: 'absolute', top: spacing.$16 }}
                />
              )}
              {renderContentByWizardStep()}
            </ProductDetailFlowContent>
          </Animated.View>
        </Animated.View>
      </LayoutAnimationConfig>
      {/* Confirmation Dialogs */}
      <CloseConfirmationDialog />
    </>
  );
};
